import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getBalance, getBalanceHolds } from "../api/balance";
import { getBalanceAnalytics, getAffiliateAnalytics } from "../api/balance";
import { getMyPayouts, getPayoutDetails } from "../api/payouts";
import { getMyReferrals } from "../api/referrals";
import WithdrawModal from "../components/WithdrawModal";
import SubscriptionsPanel from "../components/SubscriptionsPanel";
import BalanceSummary from "../components/balance/BalanceSummary";
import TransactionRow from "../components/balance/TransactionRow";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";
import StatCell from "../components/ds/StatCell";
import { useTranslation } from "../context/TranslationContext";

const RECENT_LIMIT = 6;
const FAILED_PAYOUTS = ["REJECTED", "FAILED", "DELETED", "INITIAL_INSUFFICIENT_BALANCE"];

const BalancePage = ({ isDark, setToast }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState({ amount: 0, holdBalance: 0 });
  /** What's on hold, soonest release first, and how long this person's earnings wait. */
  const [holdInfo, setHoldInfo] = useState({ holds: [] });
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  /** The smallest withdrawal allowed, as the server reports it. */
  const [minWithdrawal, setMinWithdrawal] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [affiliateAnalytics, setAffiliateAnalytics] = useState(null);
  /** People this user invited to Mintly, and what that has earned them. */
  const [invites, setInvites] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!(user?.id ?? user?._id)) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const balanceData = await getBalance(user?.id ?? user?._id);
        const amount = balanceData?.amount ?? 0;
        const holdBalance = balanceData?.holdBalance ?? balanceData?.pendingAmount ?? 0;
        setBalance({ amount: Number(amount) || 0, holdBalance: Number(holdBalance) || 0 });

        // The floor is set by an admin, so it is read rather than assumed here.
        try {
          const details = await getPayoutDetails();
          setMinWithdrawal(Number(details?.data?.fee?.minAmount) || 0);
        } catch {
          // Leave it at 0: the withdrawal itself still enforces the real figure.
        }
        
        try {
          const analyticsData = await getBalanceAnalytics(user?.id ?? user?._id);
          setAnalytics(analyticsData?.data || analyticsData);
        } catch (analyticsError) {
          console.error("Error fetching analytics:", analyticsError);
          setAnalytics({
            totalEarned: 0,
            totalSubmissions: 0,
            activeCampaigns: 0,
            campaignBreakdown: [],
          });
        }

        getMyReferrals()
          .then((res) => setInvites(res?.data || null))
          .catch(() => setInvites(null));

        try {
          const affiliateData = await getAffiliateAnalytics(user?.id ?? user?._id);
          setAffiliateAnalytics(affiliateData);
        } catch {
          setAffiliateAnalytics({
            referrals: 0,
            linkClicks: 0,
            purchases: 0,
            totalEarned: 0,
          });
        }
      } catch (error) {
        console.error("Error fetching balance data:", error);
        setToast(t("toasts.failedToLoadBalance"));
        setBalance({ amount: 0, holdBalance: 0 });
        setAnalytics({
          totalEarned: 0,
          totalSubmissions: 0,
          activeCampaigns: 0,
          campaignBreakdown: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id, user?._id, setToast, t]);

  useEffect(() => {
    const uid = user?.id ?? user?._id;
    if (!uid) return;
    const onBalanceUpdated = () => {
      getBalance(uid).then((balanceData) => {
        const amount = balanceData?.amount ?? 0;
        const holdBalance = balanceData?.holdBalance ?? balanceData?.pendingAmount ?? 0;
        setBalance({ amount: Number(amount) || 0, holdBalance: Number(holdBalance) || 0 });
      });
    };
    const loadPayouts = () =>
      getMyPayouts()
        .then((res) => setPayouts(res?.data || []))
        .catch(() => setPayouts([]));
    // Without it the page still works: On hold shows its plain note.
    const loadHolds = () =>
      getBalanceHolds(uid)
        .then((info) => info && setHoldInfo(info))
        .catch(() => setHoldInfo({ holds: [] }));
    loadPayouts();
    loadHolds();

    const onAny = () => {
      onBalanceUpdated();
      loadPayouts();
      loadHolds();
    };
    window.addEventListener("balance-updated", onAny);
    return () => window.removeEventListener("balance-updated", onAny);
  }, [user?.id, user?._id]);

  const formatDate = (dateString) => {
    if (!dateString) return t("balancePage.dateNA");
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /** What the money did lately, newest first: withdrawals, and rewards approved. */
  const transactions = useMemo(() => {
    const out = payouts.map((payout) => {
      const settled = payout.status === "SUCCESS";
      const failed = FAILED_PAYOUTS.includes(payout.status);
      return {
        id: payout._id,
        icon: "upload",
        title: t("balancePage.withdrawal"),
        meta: [payout.destination?.iban ? `•• ${payout.destination.iban.slice(-4)}` : "", formatDate(payout.createdAt)]
          .filter(Boolean)
          .join(" · "),
        detail: failed ? payout.failReason : "",
        at: payout.createdAt,
        amount: `−${(payout.amount ?? 0).toFixed(2)} ₾`,
        status: settled ? null : failed ? (payout.refunded ? "failedRefunded" : "failed") : "inProgress",
        tone: failed ? "danger" : "warning",
      };
    });
    const earned = (analytics?.campaignBreakdown || []).flatMap((campaign) =>
      (campaign.submissions || []).map((submission) => ({
        id: submission._id,
        icon: "megaphone",
        title: t("balancePage.creatorReward"),
        meta: [campaign.campaignName, formatDate(submission.approvedAt)].filter(Boolean).join(" · "),
        at: submission.approvedAt,
        amount: `+${(submission.totalEarned ?? 0).toFixed(2)} ₾`,
        incoming: true,
        status: holdInfo.holds.some((hold) => hold.referenceId === String(submission._id)) ? "onHold" : null,
        tone: "warning",
      }))
    );
    return [...out, ...earned].sort((x, y) => new Date(y.at || 0) - new Date(x.at || 0));
  }, [payouts, analytics, holdInfo, t]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Page>
        <p className="text-subheadline text-label-secondary">{t("balancePage.loading")}</p>
      </Page>
    );
  }

  const heading = (text) => <h2 className="text-[20px] font-semibold leading-[28px] text-label-primary">{text}</h2>;

  return (
    <Page>
      <PageHeader title={t("balancePage.title")} subtitle={t("balancePage.subtitle")} />

      <BalanceSummary
        available={balance?.amount ?? 0}
        onHold={balance?.holdBalance ?? 0}
        holds={holdInfo.holds}
        holdDays={holdInfo.holdDays}
        minWithdrawal={minWithdrawal}
        onWithdraw={() => setWithdrawOpen(true)}
        onTopUp={() => navigate("/payment")}
      />

      {transactions.length > 0 && (
        <section className="flex flex-col gap-space-12">
          <div className="flex items-center justify-between gap-space-12">
            {heading(t("balancePage.recentTransactions"))}
            {transactions.length > RECENT_LIMIT && (
              <Button variant="plain" size="s" onClick={() => setShowAllTransactions((all) => !all)}>
                {showAllTransactions ? t("mintSpace.showLess") : t("common.seeAll")}
              </Button>
            )}
          </div>
          <ul className="flex flex-col rounded-radius-lg bg-bg-raised p-space-8">
            {(showAllTransactions ? transactions : transactions.slice(0, RECENT_LIMIT)).map((row) => (
              <TransactionRow
                key={row.id}
                icon={row.icon}
                title={row.title}
                meta={row.meta}
                detail={row.detail}
                amount={row.amount}
                incoming={row.incoming}
                status={row.status && t(`balancePage.payoutStatus.${row.status}`)}
                tone={row.tone}
                completedLabel={t("balancePage.completed")}
              />
            ))}
          </ul>
        </section>
      )}

      {analytics && (
        <section className="flex flex-col gap-space-16">
          {heading(t("balancePage.creatorRewardsTitle") || "Creator Rewards earnings")}
          <div className="grid grid-cols-1 gap-space-16 min-[700px]:grid-cols-3 min-[700px]:gap-space-24">
            <StatCell label={t("balancePage.totalEarned")} value={`${(analytics.totalEarned || 0).toFixed(2)} ₾`} />
            <StatCell
              label={t("balancePage.creatorRewardsSubmissions") || "Creator Rewards submissions"}
              value={analytics.totalSubmissions || 0}
            />
            <StatCell label={t("balancePage.activeCampaigns")} value={analytics.activeCampaigns || 0} />
          </div>
        </section>
      )}

      {affiliateAnalytics && (
        <section className="flex flex-col gap-space-16">
          {heading(t("balancePage.affiliateTitle") || "Affiliate earnings")}
          <div className="grid grid-cols-2 gap-space-16 min-[900px]:grid-cols-4 min-[900px]:gap-space-24">
            <StatCell label={t("balancePage.affiliateReferrals") || "Referrals"} value={affiliateAnalytics.referrals || 0} />
            <StatCell label={t("balancePage.affiliateClicks") || "Link clicks"} value={affiliateAnalytics.linkClicks || 0} />
            <StatCell label={t("balancePage.affiliatePurchases") || "Purchases"} value={affiliateAnalytics.purchases || 0} />
            <StatCell label={t("balancePage.totalEarned")} value={`${(affiliateAnalytics.totalEarned || 0).toFixed(2)} ₾`} />
          </div>
        </section>
      )}

      {/* Inviting people to Mintly — the link itself lives on /referrals */}
      {invites && (
        <section className="flex flex-col gap-space-16">
          <div className="flex flex-wrap items-center justify-between gap-space-8">
            {heading(t("referrals.balanceTitle"))}
            <Button variant="tinted" size="s" icon={<Icon name="link" size={16} />} onClick={() => navigate("/referrals")}>
              {t("referrals.getLink")}
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-space-16 min-[700px]:grid-cols-2 min-[700px]:gap-space-24">
            <StatCell label={t("referrals.invited")} value={invites.invitedCount || 0} />
            <StatCell label={t("balancePage.totalEarned")} value={`${(invites.earned || 0).toFixed(2)} ₾`} />
          </div>
        </section>
      )}

      {analytics?.campaignBreakdown?.length > 0 ? (
        <section className="flex flex-col gap-space-12">
          {heading(t("balancePage.earningsByCampaign"))}
          <ul className="flex flex-col rounded-radius-lg bg-bg-raised p-space-8">
            {analytics.campaignBreakdown.map((campaign) => (
              <li key={campaign.campaignId} className="flex flex-col gap-space-4 rounded-[20px] p-space-8">
                <div className="flex items-center gap-space-12">
                  <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                    <p className="truncate text-subheadline-semibold text-label-primary">{campaign.campaignName}</p>
                    <p className="text-footnote text-label-tertiary">
                      {campaign.submissionCount || 0}{" "}
                      {campaign.submissionCount === 1 ? t("balancePage.submission") : t("balancePage.submissions")}
                    </p>
                  </div>
                  <p className="shrink-0 text-subheadline-semibold text-accent-text">{campaign.totalEarned?.toFixed(2) || "0.00"} ₾</p>
                </div>
                {campaign.submissions?.map((submission) => (
                  <div key={submission._id} className="flex items-center gap-space-12 rounded-radius-sm bg-bg-inset px-space-12 py-space-8">
                    <div className="flex min-w-0 flex-1 flex-col">
                      <p className="truncate text-footnote-semibold capitalize text-label-primary">{submission.platform}</p>
                      <p className="truncate text-footnote text-label-tertiary">
                        {submission.viewCount?.toLocaleString() || 0} {t("balancePage.views")}
                        {submission.approvedAt ? ` · ${t("balancePage.approved")} ${formatDate(submission.approvedAt)}` : ""}
                      </p>
                    </div>
                    <p className="shrink-0 text-footnote-semibold text-accent-text">{submission.totalEarned?.toFixed(2) || "0.00"} ₾</p>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        analytics && <EmptyState icon="wallet" title={t("balancePage.noEarningsYet")} message={t("balancePage.noEarningsHint")} />
      )}

      <SubscriptionsPanel isDark={isDark} setToast={setToast} />

      <WithdrawModal
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        available={balance?.amount ?? 0}
        isDark={isDark}
      />
    </Page>
  );
};

export default BalancePage;
